import { ConfirmationDialog } from '@/components/patterns/confirmation-dialog'
import { AdminHeaderActionButton, AdminPageHeader } from '@/components/patterns/admin-page'
import { OrganizationContextNotice } from '@/components/patterns/info-notice'
import { EmptyState } from '@/components/patterns/empty-state'
import {
  IndexDataTable,
  IndexTableClearFiltersButton,
  IndexTableSearchBox,
  IndexTableToolbar,
} from '@/components/tables/IndexTable'
import { FileDropzone } from '@/components/ui/file-dropzone'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Badge, BadgeButton } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Typography } from '@/components/ui/typography'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { useOrganizationContext } from '@/hooks/useOrganizationContext'
import { usePathHelper } from '@/hooks/usePathHelper'
import { OrganizationType } from '@/lib/enums/OrganizationType'
import dayjs from '@/lib/dayjs'
import {
  invalidateCustomerImportQueries,
  useActiveCustomerImportAttemptQuery,
  useCustomerImportAttemptQuery,
  useImportCustomerImportMutation,
  usePreviewCustomerImportMutation,
} from '@/lib/services/imports/imports.query'
import { customerImportRequestSchema } from '@/lib/services/imports/imports.schema'
import { cn, getErrorMessage, normalizePhoneDigits } from '@/lib/utils'
import { createFileRoute, Link } from '@tanstack/react-router'
import { AlertCircle, ArrowLeft, SlidersHorizontal, Upload, Users } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { toast } from 'sonner'

const statusBadgeVariant = {
  ready: 'info',
  imported: 'success',
  skipped: 'warning',
  invalid: 'destructive',
}

const statusBadgeLabel = {
  ready: 'Ready',
  imported: 'Imported',
  skipped: 'Skipped',
  invalid: 'Invalid',
}

const statusTooltipVariant = {
  skipped: 'warning',
  invalid: 'destructive',
}

const previewStatusFilterOptions = [
  { value: 'all', label: 'All statuses', icon: <SlidersHorizontal className="size-4" /> },
  { value: 'ready', label: 'Ready' },
  { value: 'imported', label: 'Imported' },
  { value: 'skipped', label: 'Skipped' },
  { value: 'invalid', label: 'Invalid' },
]
const previewDefaultPageSize = 50
const defaultCustomerImportSourceSystem = 'fsn'
const customerImportSourceOptions = [
  { value: 'fsn', label: 'Flower Shop Network' },
  { value: 'the_floral_pos', label: 'The Floral POS' },
  { value: 'ftd', label: 'FTD' },
  { value: 'floranext', label: 'FloraNext' },
  { value: 'quickflora', label: 'QuickFlora' },
  { value: 'bloomnation', label: 'BloomNation' },
  { value: 'generic_customer_csv', label: 'Generic Customer CSV' },
]
const expectedColumnGroups = [
  {
    value: 'fsn',
    label: 'Flower Shop Network',
    columns: ['Name', 'Phone', 'Email Address', 'Address', 'City', 'State', 'Zip Code'],
  },
  {
    value: 'the_floral_pos',
    label: 'The Floral POS',
    columns: [
      'CUST_ID',
      'COMPANY_NAME',
      'FIRST_NAME',
      'LAST_NAME',
      'ADDRESS',
      'CITY',
      'STATE',
      'ZIP',
      'PHONE',
      'EMAIL',
      'NOTES',
      'BUSINESS',
      'CUST_SINCE',
    ],
  },
  {
    value: 'ftd',
    label: 'FTD',
    columns: [
      'First Name',
      'Last Name',
      'Phone',
      'Email',
      'Address',
      'Apt/Suite/Unit',
      'City',
      'Zip/Postal',
      'State',
      'AVG Spend',
      'Tax Exempt',
      'Discount',
      'House Account Status',
      'Credit Limit',
      'Accounting Type',
      'Invoice Preference',
      'Statement Preference',
      'Credit Balance',
      'Total Balance',
    ],
  },
  {
    value: 'floranext',
    label: 'FloraNext',
    columns: [
      'Name',
      '[blank header: Last Name]',
      'Company',
      'Address',
      'City',
      'State',
      'Zipcode',
      'Email',
      'Phone1',
      'Phone2',
      'Customer Since',
      'Date of Last Order',
      'Lifetime Sales',
      'Average Sales',
      'Email Marketing',
    ],
  },
  {
    value: 'quickflora',
    label: 'QuickFlora',
    columns: [
      'Customer ID',
      'Customer Name',
      'Phone',
      'Email',
      'Address 1',
      'Address 2',
      'City',
      'State',
      'Zip',
      'Country',
      'Fax',
      'Type',
      'Company',
      'Credit Limit',
      'Since',
      'Trade Discount',
      'Price Type',
      'RM',
      'Contact Email',
      'Contact Phone',
      'Import Notes',
    ],
  },
  {
    value: 'bloomnation',
    label: 'BloomNation',
    columns: ['ID', 'Name', 'Email', 'Phone', 'Tax ID', 'Notes', 'Contact Type'],
  },
  {
    value: 'generic_customer_csv',
    label: 'Generic Customer CSV',
    columns: [
      'Name',
      'Customer Type',
      'Phone',
      'Email Address',
      'Address',
      'City',
      'State',
      'Zip Code',
      'Customer Since',
      'Accepts Marketing',
      'Tax Exempt',
      'Pay Later Allowed',
      'Allow Fulfillment Before Payment',
      'Billing Email',
      'Credit Limit',
      'Payment Terms',
      'Payment Terms Days',
      'Primary Contact Name',
      'Primary Contact Email',
      'Primary Contact Phone',
      'Notes',
      'Existing Balance',
    ],
  },
]

function createImportAttemptId() {
  if (globalThis.crypto?.randomUUID) {
    return globalThis.crypto.randomUUID()
  }

  const randomHex = (length) =>
    Array.from({ length }, () => Math.floor(Math.random() * 16).toString(16)).join('')

  return `${randomHex(8)}-${randomHex(4)}-4${randomHex(3)}-${(
    8 + Math.floor(Math.random() * 4)
  ).toString(16)}${randomHex(3)}-${randomHex(12)}`
}

export const Route = createFileRoute('/_authenticated/$orgId/imports/customer-import')({
  component: CustomerImportPage,
})

function SummaryCard({ label, value }) {
  return (
    <Card>
      <CardHeader className="space-y-1">
        <CardDescription>{label}</CardDescription>
        <CardTitle className="text-3xl">{value}</CardTitle>
      </CardHeader>
    </Card>
  )
}

function CustomerImportProgressCard({ attempt }) {
  if (!attempt) {
    return null
  }

  const isFailed = attempt.status === 'failed'
  const isCompleted = attempt.status === 'completed'
  const progressLabel =
    attempt.progress.total > 0
      ? `${attempt.progress.processed} of ${attempt.progress.total} customers`
      : 'Preparing import'
  const title = isCompleted ? 'Import Complete' : isFailed ? 'Import Failed' : 'Import Running'

  return (
    <Card surface={isFailed ? 'destructive' : 'default'}>
      <CardHeader className="space-y-2">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <CardTitle>{title}</CardTitle>
            <CardDescription>{attempt.message ?? attempt.stage_label}</CardDescription>
          </div>
          <Badge variant={isCompleted ? 'success' : isFailed ? 'destructive' : 'info'}>
            {attempt.stage_label}
          </Badge>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        <div
          className="h-3 overflow-hidden rounded-full bg-muted"
          role="progressbar"
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={attempt.progress.percent}
          aria-label="Customer import progress"
        >
          <div
            className={cn(
              'h-full rounded-full transition-[width] duration-300',
              isFailed ? 'bg-destructive' : 'bg-primary'
            )}
            style={{ width: `${attempt.progress.percent}%` }}
          />
        </div>
        <div className="flex flex-wrap items-center justify-between gap-2 text-sm text-muted-foreground">
          <span>{progressLabel}</span>
          <span>{attempt.progress.percent}%</span>
        </div>
        {attempt.error?.message ? (
          <p className="text-sm text-destructive">{attempt.error.message}</p>
        ) : null}
      </CardContent>
    </Card>
  )
}

function formatAddress(address) {
  if (!address) return 'No address'

  return [address.line1, address.city, address.state, address.postal_code]
    .filter(Boolean)
    .join(', ')
}

function formatCustomerType(customer) {
  return customer.is_business ? 'Business' : 'Individual'
}

function getPrimaryContact(customer) {
  const contacts = customer.contacts ?? []

  return contacts.find((contact) => contact.is_primary) ?? contacts[0] ?? null
}

function formatContact(customer) {
  const contact = getPrimaryContact(customer)

  if (!contact) {
    return 'None'
  }

  const details = [contact.email, contact.phone].filter(Boolean).join(', ')

  return details ? `${contact.name} (${details})` : contact.name
}

function formatCustomerSince(customer) {
  return customer.customer_since_on
    ? dayjs(customer.customer_since_on).format('MMM D, YYYY')
    : 'None'
}

function getStatusDisplayLabel(status) {
  return statusBadgeLabel[status] ?? status
}

function getIssueText(row) {
  return row.reason ?? ''
}

function getRowPreviewPresentation(row) {
  if (row.status === 'ready' && row.reason) {
    return { tone: 'warning' }
  }

  if (row.status === 'invalid') {
    return { tone: 'destructive' }
  }

  if (row.status === 'skipped') {
    return { tone: 'warning' }
  }

  if (row.status === 'imported') {
    return { tone: 'success' }
  }

  return { tone: 'neutral' }
}

function StatusBadgeCell({ row }) {
  const label = getStatusDisplayLabel(row.status)
  const tooltipMessage =
    row.reason && (row.status === 'invalid' || row.status === 'skipped' || row.status === 'ready')
      ? row.reason
      : null
  const badge = <Badge variant={statusBadgeVariant[row.status] ?? 'secondary'}>{label}</Badge>

  if (!tooltipMessage) {
    return badge
  }

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <BadgeButton
          variant={statusBadgeVariant[row.status] ?? 'secondary'}
          aria-label={`${label}: ${tooltipMessage}`}
        >
          {label}
        </BadgeButton>
      </TooltipTrigger>
      <TooltipContent variant={statusTooltipVariant[row.status] ?? 'default'}>
        {tooltipMessage}
      </TooltipContent>
    </Tooltip>
  )
}

const previewColumns = [
  {
    accessorKey: 'row_number',
    header: 'Row',
    meta: {
      label: 'Row',
      kind: 'number',
    },
  },
  {
    id: 'status',
    accessorFn: (row) => getStatusDisplayLabel(row.status),
    header: 'Status',
    meta: {
      label: 'Status',
      kind: 'badge',
    },
    cell: ({ row }) => <StatusBadgeCell row={row.original} />,
  },
  {
    id: 'name',
    accessorFn: (row) => row.customer.name ?? '',
    header: 'Name',
    meta: {
      label: 'Name',
      kind: 'primary',
    },
    cell: ({ row }) => row.original.customer.name ?? 'Missing name',
  },
  {
    id: 'type',
    accessorFn: (row) => formatCustomerType(row.customer),
    header: 'Type',
    meta: {
      label: 'Type',
      kind: 'text',
    },
    cell: ({ row }) => formatCustomerType(row.original.customer),
  },
  {
    id: 'contact',
    accessorFn: (row) => formatContact(row.customer),
    header: 'Contact',
    meta: {
      label: 'Contact',
      kind: 'text',
      preferredWidth: '16rem',
      minWidth: '12rem',
    },
    cell: ({ row }) => formatContact(row.original.customer),
  },
  {
    id: 'source',
    accessorFn: (row) => row.customer.source_key ?? '',
    header: 'Source ID',
    meta: {
      label: 'Source ID',
      kind: 'text',
    },
    cell: ({ row }) => row.original.customer.source_key ?? 'None',
  },
  {
    id: 'customer_since',
    accessorFn: (row) => row.customer.customer_since_on ?? '',
    header: 'Customer Since',
    meta: {
      label: 'Customer Since',
      kind: 'date',
    },
    cell: ({ row }) => formatCustomerSince(row.original.customer),
  },
  {
    id: 'email',
    accessorFn: (row) => row.customer.email ?? '',
    header: 'Email',
    meta: {
      label: 'Email',
      kind: 'text',
    },
    cell: ({ row }) => row.original.customer.email ?? 'None',
  },
  {
    id: 'phone',
    accessorFn: (row) => row.customer.phone ?? '',
    header: 'Phone',
    meta: {
      label: 'Phone',
      kind: 'text',
    },
    cell: ({ row }) => row.original.customer.phone ?? 'None',
  },
  {
    id: 'address',
    accessorFn: (row) => formatAddress(row.customer.physical_address),
    header: 'Address',
    meta: {
      label: 'Address',
      kind: 'text',
      preferredWidth: '18rem',
      minWidth: '14rem',
    },
    cell: ({ row }) => formatAddress(row.original.customer.physical_address),
  },
  {
    id: 'issue',
    accessorFn: (row) => getIssueText(row),
    header: 'Issue',
    meta: {
      label: 'Issue',
      kind: 'text',
      preferredWidth: '24rem',
      minWidth: '18rem',
    },
    cell: ({ row }) => getIssueText(row.original),
  },
]

function getAddressSearchText(address) {
  if (!address) {
    return ''
  }

  return [address.line1, address.city, address.state, address.postal_code].filter(Boolean).join(' ')
}

function getContactSearchText(customer) {
  return (customer.contacts ?? [])
    .flatMap((contact) => [contact.name, contact.email, contact.phone])
    .filter(Boolean)
    .join(' ')
}

function normalizeSearchText(value) {
  return String(value ?? '')
    .trim()
    .toLowerCase()
}

function matchesPreviewSearch(row, searchTerm) {
  if (!searchTerm) {
    return true
  }

  const customer = row.customer ?? {}
  const normalizedPhoneDigits = [
    customer.phone,
    ...(customer.contacts ?? []).map((contact) => contact.phone),
  ]
    .map((phone) => normalizePhoneDigits(phone))
    .join(' ')
  const normalizedSearchDigits = normalizePhoneDigits(searchTerm)
  const searchableValues = [
    customer.name,
    customer.email,
    customer.phone,
    customer.source_key,
    customer.customer_since_on,
    formatCustomerType(customer),
    getContactSearchText(customer),
    getAddressSearchText(customer.physical_address),
    row.reason,
  ]

  if (searchableValues.some((value) => normalizeSearchText(value).includes(searchTerm))) {
    return true
  }

  return (
    normalizedSearchDigits.length >= 3 && normalizedPhoneDigits.includes(normalizedSearchDigits)
  )
}

function filterPreviewRows(rows, searchTerm, statusFilter) {
  return rows.filter((row) => {
    if (statusFilter !== 'all' && row.status !== statusFilter) {
      return false
    }

    return matchesPreviewSearch(row, searchTerm)
  })
}

function getPreviewRowCountText(filteredCount, totalCount) {
  if (filteredCount === totalCount) {
    return `${totalCount} row${totalCount === 1 ? '' : 's'}`
  }

  return `${filteredCount} of ${totalCount} rows`
}

function getImportErrorDescription(error, fallbackMessage) {
  const fieldErrors = error?.response?.data?.errors
  const firstFieldMessage = fieldErrors
    ? Object.values(fieldErrors)
        .flat()
        .find((message) => typeof message === 'string' && message.length > 0)
    : null

  if (firstFieldMessage) {
    return firstFieldMessage
  }

  if (Array.isArray(error?.issues) && error.issues.length > 0) {
    return 'The server returned unexpected import data. Please try again.'
  }

  const message = getErrorMessage(error)

  if (typeof message !== 'string' || message.trim() === '') {
    return fallbackMessage
  }

  return message.length > 220 ? `${message.slice(0, 217)}...` : message
}

export function CustomerImportPage() {
  const { organization } = useOrganizationContext()
  const path = usePathHelper()
  const isGlobalOrg = organization?.type === OrganizationType.Global.Value
  const previewMutation = usePreviewCustomerImportMutation()
  const importMutation = useImportCustomerImportMutation()
  const [selectedFile, setSelectedFile] = useState(null)
  const [sourceSystem, setSourceSystem] = useState(defaultCustomerImportSourceSystem)
  const [previewResult, setPreviewResult] = useState(null)
  const [previewContext, setPreviewContext] = useState(null)
  const [previewSearchTerm, setPreviewSearchTerm] = useState('')
  const [previewStatusFilter, setPreviewStatusFilter] = useState('all')
  const [isConfirmOpen, setIsConfirmOpen] = useState(false)
  const [errorState, setErrorState] = useState(null)
  const [activeImportAttemptId, setActiveImportAttemptId] = useState(null)
  const previewRequestVersionRef = useRef(0)
  const handledImportAttemptIdsRef = useRef(new Set())
  const detachedImportAttemptIdsRef = useRef(new Set())
  const [completedImportAttemptIds, setCompletedImportAttemptIds] = useState(() => new Set())
  const activeCustomerImportAttemptQuery = useActiveCustomerImportAttemptQuery({
    enabled: Boolean(organization?.id && !isGlobalOrg),
  })
  const refetchActiveCustomerImportAttempt = activeCustomerImportAttemptQuery.refetch
  const importAttemptQuery = useCustomerImportAttemptQuery(activeImportAttemptId, {
    enabled: Boolean(activeImportAttemptId),
  })
  const rawRecoveredImportAttempt = activeCustomerImportAttemptQuery.data ?? null
  const recoveredImportAttempt =
    rawRecoveredImportAttempt?.id &&
    // oxlint-disable-next-line react/refs -- grandfathered
    (handledImportAttemptIdsRef.current.has(rawRecoveredImportAttempt.id) ||
      // oxlint-disable-next-line react/refs -- grandfathered
      detachedImportAttemptIdsRef.current.has(rawRecoveredImportAttempt.id))
      ? null
      : rawRecoveredImportAttempt
  const recoveredImportAttemptId = recoveredImportAttempt?.id ?? null
  const activeImportAttempt = importAttemptQuery.data ?? null
  const importMutationAttempt =
    previewContext?.importAttemptId && importMutation.data?.id === previewContext.importAttemptId
      ? importMutation.data
      : null
  const displayedImportAttempt =
    activeImportAttempt ??
    recoveredImportAttempt ??
    (activeImportAttemptId ? importMutationAttempt : null)
  const activeImportStatus =
    activeImportAttempt?.status ?? recoveredImportAttempt?.status ?? importMutationAttempt?.status
  const isImportRunning =
    importMutation.isPending || activeImportStatus === 'queued' || activeImportStatus === 'running'

  const hasReadyRows = (previewResult?.summary?.ready ?? 0) > 0
  const hasCurrentPreview =
    hasReadyRows &&
    previewContext?.file === selectedFile &&
    previewContext?.sourceSystem === sourceSystem
  const currentPreviewAlreadyImported = Boolean(
    previewContext?.importAttemptId && completedImportAttemptIds.has(previewContext.importAttemptId)
  )
  const previewRows = previewResult?.rows ?? []
  const previewRowsReturned = previewResult?.rows_returned ?? previewRows.length
  const previewRowsTruncated = previewResult?.rows_truncated ?? false
  const normalizedPreviewSearchTerm = normalizeSearchText(previewSearchTerm)
  const filteredPreviewRows = filterPreviewRows(
    previewRows,
    normalizedPreviewSearchTerm,
    previewStatusFilter
  )
  const hasPreviewFilters = normalizedPreviewSearchTerm !== '' || previewStatusFilter !== 'all'
  const previewTableNeedsScrollFrame = filteredPreviewRows.length > 15

  useEffect(() => {
    if (!recoveredImportAttemptId || activeImportAttemptId === recoveredImportAttemptId) {
      return
    }

    // oxlint-disable-next-line react/set-state-in-effect -- grandfathered
    setActiveImportAttemptId(recoveredImportAttemptId)
    setSourceSystem(recoveredImportAttempt.source_system)
    setErrorState(null)
  }, [activeImportAttemptId, recoveredImportAttempt, recoveredImportAttemptId])

  useEffect(() => {
    if (!activeImportAttempt || handledImportAttemptIdsRef.current.has(activeImportAttempt.id)) {
      return
    }

    const isCurrentPreviewAttempt =
      activeImportAttempt.id === previewContext?.importAttemptId &&
      previewContext.file === selectedFile &&
      previewContext.sourceSystem === sourceSystem
    const isRecoveredAttempt =
      activeImportAttempt.id === recoveredImportAttemptId &&
      !detachedImportAttemptIdsRef.current.has(activeImportAttempt.id)

    if (!isCurrentPreviewAttempt && !isRecoveredAttempt) {
      return
    }

    if (activeImportAttempt.status === 'completed') {
      handledImportAttemptIdsRef.current.add(activeImportAttempt.id)
      // oxlint-disable-next-line react/set-state-in-effect -- grandfathered
      setCompletedImportAttemptIds((previousIds) => {
        if (previousIds.has(activeImportAttempt.id)) {
          return previousIds
        }

        const nextIds = new Set(previousIds)
        nextIds.add(activeImportAttempt.id)

        return nextIds
      })

      if (activeImportAttempt.result) {
        setPreviewResult(activeImportAttempt.result)
        setPreviewSearchTerm('')
        setPreviewStatusFilter('all')
      }

      setErrorState(null)

      if (organization?.id) {
        void invalidateCustomerImportQueries({ organizationId: organization.id })
      }
      void refetchActiveCustomerImportAttempt?.()

      toast.success('Customers imported.', {
        description: activeImportAttempt.result
          ? `${activeImportAttempt.result.summary.created} customer${activeImportAttempt.result.summary.created === 1 ? '' : 's'} created.`
          : 'The import finished successfully.',
      })

      return
    }

    if (activeImportAttempt.status === 'failed') {
      handledImportAttemptIdsRef.current.add(activeImportAttempt.id)
      void refetchActiveCustomerImportAttempt?.()
      const description =
        activeImportAttempt.error?.message ??
        activeImportAttempt.message ??
        'Please review the spreadsheet and try again.'

      setErrorState({
        title: 'Import failed',
        message: description,
      })
      toast.error('Unable to import customers.', {
        description,
      })
    }
  }, [
    activeImportAttempt,
    organization?.id,
    previewContext,
    recoveredImportAttemptId,
    refetchActiveCustomerImportAttempt,
    selectedFile,
    sourceSystem,
  ])

  const validateSelectedFile = () =>
    customerImportRequestSchema.safeParse({
      file: selectedFile,
      source_system: sourceSystem,
    })
  const invalidatePreviewRequest = () => {
    previewRequestVersionRef.current += 1
  }
  const resetPreviewUi = () => {
    if (activeImportAttemptId) {
      detachedImportAttemptIdsRef.current.add(activeImportAttemptId)
    }

    if (recoveredImportAttemptId) {
      detachedImportAttemptIdsRef.current.add(recoveredImportAttemptId)
    }

    setPreviewResult(null)
    setPreviewContext(null)
    setCompletedImportAttemptIds(new Set())
    setPreviewSearchTerm('')
    setPreviewStatusFilter('all')
    setIsConfirmOpen(false)
    setActiveImportAttemptId(null)
  }

  const handleFileSelect = (file) => {
    const validationResult = customerImportRequestSchema.safeParse({
      file,
      source_system: sourceSystem,
    })

    if (!validationResult.success) {
      const message = validationResult.error.issues[0]?.message ?? 'Choose an .xlsx or .csv file.'

      invalidatePreviewRequest()
      setSelectedFile(null)
      resetPreviewUi()
      setErrorState({
        title: 'Invalid file',
        message,
      })
      toast.error(message)

      return
    }

    invalidatePreviewRequest()
    setSelectedFile(file)
    resetPreviewUi()
    setErrorState(null)
  }

  const handleSourceSystemChange = (value) => {
    invalidatePreviewRequest()
    setSourceSystem(value)
    resetPreviewUi()
    setErrorState(null)
  }

  const handleFileClear = () => {
    invalidatePreviewRequest()
    setSelectedFile(null)
    resetPreviewUi()
    setErrorState(null)
  }

  const handlePreview = async (event) => {
    event.preventDefault()

    const validationResult = validateSelectedFile()
    if (!validationResult.success) {
      const message = validationResult.error.issues[0]?.message ?? 'Choose an .xlsx or .csv file.'

      setErrorState({
        title: 'Invalid file',
        message,
      })
      toast.error(message)

      return
    }

    const previewRequestVersion = previewRequestVersionRef.current + 1
    previewRequestVersionRef.current = previewRequestVersion
    const previewFile = selectedFile
    const previewSourceSystem = sourceSystem

    try {
      const result = await previewMutation.mutateAsync({
        file: previewFile,
        sourceSystem: previewSourceSystem,
      })

      if (previewRequestVersionRef.current !== previewRequestVersion) {
        return
      }

      setPreviewResult(result)
      setPreviewContext({
        file: previewFile,
        sourceSystem: previewSourceSystem,
        importAttemptId: createImportAttemptId(),
      })
      setPreviewSearchTerm('')
      setPreviewStatusFilter('all')
      setErrorState(null)
      toast.success('Customer import preview ready.')
    } catch (error) {
      if (previewRequestVersionRef.current !== previewRequestVersion) {
        return
      }

      const description = getImportErrorDescription(
        error,
        'Please review the spreadsheet and try again.'
      )

      setErrorState({
        title: 'Preview failed',
        message: description,
      })
      toast.error('Unable to preview customer import.', {
        description,
      })
    }
  }

  const handleImport = async (event) => {
    event.preventDefault()

    const validationResult = validateSelectedFile()
    if (!validationResult.success) {
      const message = validationResult.error.issues[0]?.message ?? 'Choose an .xlsx or .csv file.'

      setErrorState({
        title: 'Invalid file',
        message,
      })
      toast.error(message)

      return
    }

    try {
      if (!hasCurrentPreview || !previewContext?.importAttemptId) {
        const message = 'Preview this file again before importing.'

        setIsConfirmOpen(false)
        setErrorState({
          title: 'Preview needed',
          message,
        })
        toast.error(message)

        return
      }

      const importAttemptId = previewContext.importAttemptId
      if (completedImportAttemptIds.has(importAttemptId)) {
        const message = 'Preview this file again before importing more customers.'

        setIsConfirmOpen(false)
        setErrorState({
          title: 'Preview already imported',
          message,
        })
        toast.error(message)

        return
      }

      detachedImportAttemptIdsRef.current.delete(importAttemptId)
      const result = await importMutation.mutateAsync({
        file: selectedFile,
        sourceSystem,
        importAttemptId,
      })
      detachedImportAttemptIdsRef.current.delete(result.id)

      if (result.status === 'completed') {
        handledImportAttemptIdsRef.current.add(result.id)
        setCompletedImportAttemptIds((previousIds) => {
          if (previousIds.has(result.id)) {
            return previousIds
          }

          const nextIds = new Set(previousIds)
          nextIds.add(result.id)

          return nextIds
        })

        if (result.result) {
          setPreviewResult(result.result)
          setPreviewSearchTerm('')
          setPreviewStatusFilter('all')
        }

        setActiveImportAttemptId(null)
        setErrorState(null)

        if (organization?.id) {
          void invalidateCustomerImportQueries({ organizationId: organization.id })
        }
        void refetchActiveCustomerImportAttempt?.()

        toast.success('Customers imported.', {
          description: result.result
            ? `${result.result.summary.created} customer${result.result.summary.created === 1 ? '' : 's'} created.`
            : 'The import finished successfully.',
        })

        return
      }

      if (result.status === 'failed') {
        handledImportAttemptIdsRef.current.add(result.id)
        setActiveImportAttemptId(null)
        void refetchActiveCustomerImportAttempt?.()
        const description =
          result.error?.message ?? result.message ?? 'Please review the spreadsheet and try again.'

        setErrorState({
          title: 'Import failed',
          message: description,
        })
        toast.error('Unable to import customers.', {
          description,
        })

        return
      }

      setActiveImportAttemptId(result.id)
      setErrorState(null)
      toast.success('Customer import started.', {
        description:
          result.progress.total > 0
            ? `${result.progress.total} customer${result.progress.total === 1 ? ' is' : 's are'} queued for import.`
            : 'The import is queued.',
      })
    } catch (error) {
      const description = getImportErrorDescription(
        error,
        'Please review the spreadsheet and try again.'
      )

      setErrorState({
        title: 'Import failed',
        message: description,
      })
      toast.error('Unable to import customers.', {
        description,
      })
    } finally {
      setIsConfirmOpen(false)
    }
  }

  if (isGlobalOrg) {
    return (
      <div className="max-w-6xl space-y-4">
        <AdminPageHeader
          icon={Users}
          title="Customer Import"
          description="Preview and import customer rows into the shop you are impersonating."
          actions={
            <AdminHeaderActionButton asChild variant="outline">
              <Link {...path.imports.index().link}>
                <ArrowLeft className="size-4" />
                Back to Imports
              </Link>
            </AdminHeaderActionButton>
          }
        />

        <OrganizationContextNotice title="Choose an organization first">
          Customer imports only run against the florist account you are currently impersonating.
          Choose the correct shop before you upload a file.
        </OrganizationContextNotice>
      </div>
    )
  }

  return (
    <div className="max-w-6xl space-y-4">
      <AdminPageHeader
        icon={Users}
        title="Customer Import"
        description={`Preview and import customer rows into ${organization?.name ?? 'this shop'}. Existing matches are skipped; nothing is updated in place.`}
        actions={
          <AdminHeaderActionButton asChild variant="outline">
            <Link {...path.imports.index().link}>
              <ArrowLeft className="size-4" />
              Back to Imports
            </Link>
          </AdminHeaderActionButton>
        }
      />

      <Card>
        <CardHeader>
          <CardTitle>Upload Customer File</CardTitle>
          <CardDescription>
            Use the customer list for this shop. Preview the file before anything changes.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-1">
            <label className="text-sm font-medium" htmlFor="customer-import-source-system">
              Import Format
            </label>
            <Select value={sourceSystem} onValueChange={handleSourceSystemChange}>
              <SelectTrigger
                id="customer-import-source-system"
                aria-label="Customer import format"
                size="toolbar"
                className="w-full max-w-72"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {customerImportSourceOptions.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <FileDropzone
            id="customer-import-file"
            label="Customer import file (.xlsx or .csv)"
            accept=".xlsx,.csv"
            file={selectedFile}
            onFileSelect={handleFileSelect}
            onClear={handleFileClear}
            description="XLSX or CSV. The first worksheet is read for spreadsheets."
          />

          <details className="border-t border-border/70 pt-3">
            <summary className="cursor-pointer text-sm font-medium text-muted-foreground hover:text-foreground">
              Show file columns
            </summary>
            <div className="mt-2 space-y-2">
              {expectedColumnGroups.map((group) => (
                <Typography
                  variant="supporting"
                  tone={group.value === sourceSystem ? 'default' : 'muted'}
                  weight={group.value === sourceSystem ? 'semibold' : 'inherit'}
                  key={group.value}
                  data-state={group.value === sourceSystem ? 'selected' : 'inactive'}
                  className="block [overflow-wrap:anywhere]"
                >
                  <span
                    className={cn(
                      'font-medium text-foreground',
                      group.value === sourceSystem && 'font-semibold'
                    )}
                  >
                    {group.label}:
                  </span>{' '}
                  {group.columns.join(', ')}
                </Typography>
              ))}
            </div>
          </details>

          {errorState ? (
            <Alert variant="destructive">
              <AlertCircle />
              <AlertTitle>{errorState.title}</AlertTitle>
              <AlertDescription>
                <p>{errorState.message}</p>
              </AlertDescription>
            </Alert>
          ) : null}

          <div className="flex flex-wrap gap-2">
            <Button
              type="button"
              onClick={handlePreview}
              disabled={!selectedFile || previewMutation.isPending || isImportRunning}
            >
              <Upload className="size-4" />
              {previewMutation.isPending ? 'Previewing...' : 'Preview Import'}
            </Button>

            <ConfirmationDialog
              open={isConfirmOpen}
              onOpenChange={setIsConfirmOpen}
              isPending={importMutation.isPending}
              variant="question"
              title="Import Customers"
              description="Create every ready customer in this shop. Existing matches stay untouched."
              confirmText="Import Customers"
              pendingText="Starting..."
              onConfirm={handleImport}
              trigger={
                <Button
                  type="button"
                  disabled={
                    !hasCurrentPreview ||
                    currentPreviewAlreadyImported ||
                    previewMutation.isPending ||
                    isImportRunning
                  }
                >
                  <Users className="size-4" />
                  {isImportRunning
                    ? 'Import Running'
                    : currentPreviewAlreadyImported
                      ? 'Imported'
                      : 'Import Customers'}
                </Button>
              }
            />
          </div>
        </CardContent>
      </Card>

      <CustomerImportProgressCard attempt={displayedImportAttempt} />

      {previewResult ? (
        <>
          <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-5">
            <SummaryCard label="Total Rows" value={previewResult.summary.total} />
            <SummaryCard label="Ready" value={previewResult.summary.ready} />
            <SummaryCard label="Imported" value={previewResult.summary.created} />
            <SummaryCard label="Skipped" value={previewResult.summary.skipped} />
            <SummaryCard label="Invalid" value={previewResult.summary.invalid} />
          </div>

          <Card>
            <CardHeader>
              <CardTitle>Row Preview</CardTitle>
              <CardDescription>
                {previewRowsTruncated
                  ? `Showing a problem-first sample of ${previewRowsReturned.toLocaleString()} of ${previewResult.summary.total.toLocaleString()} rows. Summary counts cover the full file. `
                  : 'Search, filter, and page through the preview before importing. '}
                Invalid phone values are treated as warnings and will be dropped during import.
                Hover a skipped, ready, or invalid status to see the issue inline.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <IndexDataTable
                mode="client"
                pagination
                data={filteredPreviewRows}
                columns={previewColumns}
                getRowId={(row) => String(row.row_number)}
                getRowPresentation={getRowPreviewPresentation}
                defaultPageSize={previewDefaultPageSize}
                maxPageSize={100}
                scrollAreaClassName={
                  previewTableNeedsScrollFrame ? 'h-[min(64rem,calc(100dvh-20rem))]' : undefined
                }
                toolbar={
                  <IndexTableToolbar
                    primary={
                      <IndexTableSearchBox
                        value={previewSearchTerm}
                        onCommit={setPreviewSearchTerm}
                        countText={getPreviewRowCountText(
                          filteredPreviewRows.length,
                          previewRows.length
                        )}
                        placeholder="Search name, phone, email, or address..."
                      />
                    }
                    secondary={
                      <>
                        {hasPreviewFilters ? (
                          <IndexTableClearFiltersButton
                            onClick={() => {
                              setPreviewSearchTerm('')
                              setPreviewStatusFilter('all')
                            }}
                          />
                        ) : null}
                        <Select value={previewStatusFilter} onValueChange={setPreviewStatusFilter}>
                          <SelectTrigger
                            aria-label="Filter preview rows by status"
                            selected={previewStatusFilter !== 'all'}
                            className="w-fit min-w-[11rem]"
                          >
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {previewStatusFilterOptions.map((option) => (
                              <SelectItem key={option.value} value={option.value}>
                                {option.icon}
                                {option.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </>
                    }
                  />
                }
                emptyState={
                  <EmptyState
                    title={hasPreviewFilters ? 'No matching preview rows' : 'No preview rows found'}
                    description={
                      hasPreviewFilters
                        ? 'Try a different search or status filter.'
                        : 'The selected worksheet did not produce any importable customer rows.'
                    }
                  />
                }
              />
            </CardContent>
          </Card>
        </>
      ) : null}
    </div>
  )
}
